import { QueryClient, QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { AppDashboard } from '@shared/types/app';
import dayjs from 'dayjs';

import API from '@api/API';
import { ToastNotification } from '@api/handleResponse';

import { useAnalytics } from '@hooks/useAnalytics';
import { DynamicActionProperties } from '@hooks/useDynamicAction';
import { sessionId } from '@hooks/useReferredBy';

import { ButtonProps } from '@components/buttons/Button';

import { useAppContext } from '../contexts/AppContext';
import { AppStorage, AppStorageKey, useAppStorageValue } from '../utilities/appStorage';
import { useCurrentEntertainerQuery } from './EntertainerQueries';
import { FeedEvent } from './EventQueries';
import { Promoter } from './PromoterQueries';

interface OnboardingProperties {
  isComplete: boolean;
  basicInfoIsComplete?: boolean;
  interestsAreComplete?: boolean;
  discoverIsComplete?: boolean;
  communityIsComplete?: boolean;
}

export interface CurrentUser {
  _id: string;
  avatar?: string;
  displayName?: string;
  privateDisplayName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  isSuperAdmin?: boolean;
  isDragKingCommunityAdmin?: boolean;
  notificationsEnabled?: boolean;
  smsNotificationsEnabled?: boolean;
  onboarding?: OnboardingProperties;
  entertainerId?: string;
  isAgeGateVisible?: boolean;
  isOver21?: boolean;
  sharedEntertainerCommentCount: number;
  connectionAutoAcceptKey?: string;
  connectionInviteUrl?: string;
}

export enum SplashNotificationSubmitActionType {
  YesNo = 'yesNo',
  TextInput = 'textInput',
  TextArea = 'textArea',
  MultipleChoice = 'multipleChoice',
}

export interface SplashNotification {
  isRead?: boolean;
  _id: string;
  imageUrl?: string;
  fullSizeImageUrl?: string;
  title: string;
  content: string;
  htmlContent?: string;
  displayPath: string;
  height?: 'half' | '3quarters' | 'full';
  button?: {
    text: string;
    to: string;
    action?: () => void;
  };
  submit?: {
    key?: string;
    type: SplashNotificationSubmitActionType;
    placeholder?: string;
    options?: { value: string; label: string }[];
    action: string;
    meta?: Record<string, any>;
  };
}

export interface CurrentUserResponseBody {
  token?: string;
  user: CurrentUser;
  splashNotifications: SplashNotification[];
}

export const useCurrentUser = () => {
  const { userIsLoggedIn } = useAppContext();

  const storedUser = useAppStorageValue(AppStorageKey.User);

  const { isFetched, data, refetch } = useQuery({
    queryKey: useCurrentUser.queryKey(),
    queryFn: useCurrentUser.queryFn,
    enabled: userIsLoggedIn,
  });

  return {
    currentUserIsReady: isFetched,
    currentUser: data?.user ?? storedUser,
    refetchUser: refetch,
  };
};
useCurrentUser.queryKey = (): QueryKey => ['user'];
useCurrentUser.queryFn = async () => {
  const response = await API.get<CurrentUserResponseBody>(`/v1/user/me`);

  if (response?.token) {
    localStorage.setItem('token', response.token);
  }

  await AppStorage.setItem(AppStorageKey.User, response.user);

  return response;
};

export const useSplashNotifications = () => {
  const { userIsLoggedIn } = useAppContext();

  const { isFetched, data, refetch } = useQuery({
    queryKey: useCurrentUser.queryKey(),
    queryFn: useCurrentUser.queryFn,
    enabled: userIsLoggedIn,
  });

  return {
    splashNotificationsAreReady: isFetched,
    splashNotifications: data?.splashNotifications,
    refetchSplashNotifications: refetch,
  };
};

export const useToastNotification = () => {
  const queryClient = useQueryClient();
  const { userIsLoggedIn } = useAppContext();

  const { data } = useQuery<ToastNotification>({
    queryKey: useToastNotification.queryKey(),
    queryFn: () => {
      return queryClient.getQueryData<ToastNotification>(useToastNotification.queryKey()) ?? null;
    },
    enabled: userIsLoggedIn,
  });

  return {
    toastNotificationIsReady: true,
    toastNotification: data,
  };
};
useToastNotification.queryKey = () => ['toast-notification'];
useToastNotification.queryFn = () => {};

export const fetchCurrentUser = async (queryClient: QueryClient) => {
  await queryClient.invalidateQueries({ queryKey: useCurrentUser.queryKey() });

  return queryClient.fetchQuery({
    queryKey: useCurrentUser.queryKey(),
    queryFn: useCurrentUser.queryFn,
  });
};

export const useFetchCurrentUser = () => {
  const queryClient = useQueryClient();

  return () => fetchCurrentUser(queryClient);
};

export const useIsValidTokenQuery = () => {
  const { userIsLoggedIn } = useAppContext();

  const { isFetched, data, refetch } = useQuery({
    queryKey: useIsValidTokenQuery.queryKey(),
    queryFn: useIsValidTokenQuery.queryFn,
    enabled: userIsLoggedIn,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  if (!userIsLoggedIn) {
    return {
      tokenIsValidIsReady: true,
      tokenIsValid: false,
      refetchTokenIsValid: () => {},
    };
  }

  return {
    tokenIsValidIsReady: isFetched,
    tokenIsValid: data?.tokenIsValid,
    refetchTokenIsValid: refetch,
  };
};
useIsValidTokenQuery.queryKey = () => ['tokenIsValid'];
useIsValidTokenQuery.queryFn = () =>
  API.get<{ tokenIsValid: boolean }>(`/v1/token/validate?sessionId=${sessionId}`);
export const fetchTokenIsValid = async (queryClient: QueryClient) => {
  await queryClient.invalidateQueries({ queryKey: useIsValidTokenQuery.queryKey() });

  return queryClient.fetchQuery({
    queryKey: useIsValidTokenQuery.queryKey(),
    queryFn: useIsValidTokenQuery.queryFn,
  });
};

export const useFetchTokenIsValid = () => {
  const queryClient = useQueryClient();

  return () => fetchTokenIsValid(queryClient);
};

const NOTIFICATION_TYPES = ['suggestedEvents'] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export interface Notification extends DynamicActionProperties {
  _id: string;
  text: string;
  sentAt: string;
  seenAt?: string;
  readAt?: string;
  type: NotificationType;
}

export const useNotificationsQuery = () => {
  const { currentUser } = useCurrentUser();

  const { isFetched, data, refetch } = useQuery({
    queryKey: useNotificationsQuery.queryKey(currentUser?._id),
    queryFn: () => useNotificationsQuery.queryFn(currentUser?._id),
  });

  return {
    notificationsAreReady: isFetched,
    notifications: data?.notifications,
    refetchNotifications: refetch,
  };
};
useNotificationsQuery.queryKey = (userId?: string) => [
  'notifications',
  userId ?? 'unauthenticated',
];
useNotificationsQuery.queryFn = (userId?: string) =>
  userId
    ? API.get<{ notifications: Notification[] }>(`/v1/user/notifications`)
    : Promise.resolve({ notifications: [] });

export const useFollowsQuery = () => {
  const { userIsLoggedIn } = useAppContext();

  const { isFetched, data, refetch } = useQuery({
    queryKey: useFollowsQuery.queryKey(),
    queryFn: useFollowsQuery.queryFn,
    enabled: userIsLoggedIn,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
  });

  return {
    followsAreReady: isFetched,
    follows: data?.follows,
    refetchFollows: refetch,
  };
};
useFollowsQuery.queryKey = () => ['user-follows'];
useFollowsQuery.queryFn = () =>
  API.get<{ follows: { [key: string]: { [documentId: string]: true } } }>(`/v1/user/follows`);

export const useUserInterestsQuery = () => {
  const { userIsLoggedIn } = useAppContext();

  const { isFetched, data, refetch } = useQuery({
    queryKey: useUserInterestsQuery.queryKey(),
    queryFn: () => useUserInterestsQuery.queryFn(),
    enabled: userIsLoggedIn,
  });

  return {
    userInterestsAreReady: isFetched,
    userInterests: data?.tags,
    refetchUserInterests: refetch,
  };
};
useUserInterestsQuery.queryKey = () => ['user', 'interests'];
useUserInterestsQuery.queryFn = () =>
  API.get<{ tags: { _id: string; title: string }[] }>(`/v1/user/interests`);

export const useInterestsQuery = ({ searchValue }: { searchValue: string }) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useInterestsQuery.queryKey(searchValue),
    queryFn: () => useInterestsQuery.queryFn(searchValue),
    enabled: !!searchValue,
  });

  const { track } = useAnalytics();
  useEffect(() => {
    if (searchValue) {
      track('Interests Search', { Search: searchValue });
    }
  }, [searchValue]);

  return {
    interestsAreReady: isFetched,
    interests: data?.tags,
    refetchInterests: refetch,
  };
};
useInterestsQuery.queryKey = (searchValue: string) => ['interests', searchValue];
useInterestsQuery.queryFn = (searchValue: string) =>
  API.get<{ tags: { _id: string; title: string }[] }>(`/v1/interests?s=${searchValue}`);

export const useInterestQuery = (tagId: string) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useInterestQuery.queryKey(tagId),
    queryFn: () => useInterestQuery.queryFn(tagId),
    enabled: !!tagId,
  });

  return {
    interestIsReady: isFetched,
    interest: data,
    refetchInterest: refetch,
  };
};
useInterestQuery.queryKey = (tagId: string) => ['interests', 'results', tagId];
useInterestQuery.queryFn = (tagId: string) =>
  API.get<{ tag: { _id: string; title: string }; events: any[]; venues: any[] }>(
    `/v1/user/interests/${tagId}`,
  );

export const usePopularInterestsQuery = () => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: usePopularInterestsQuery.queryKey(),
    queryFn: () => usePopularInterestsQuery.queryFn(),
  });

  return {
    popularInterestsAreReady: isFetched,
    popularInterests: data?.tags,
    refetchInterests: refetch,
  };
};
usePopularInterestsQuery.queryKey = () => ['popular-interests', dayjs().format('YYYY-MM-DD')];
usePopularInterestsQuery.queryFn = () =>
  API.get<{ tags: { _id: string; title: string }[] }>(`/v1/interests/popular`);

interface SearchParams {
  cityId?: string;
  searchValue?: string;
  startDate?: string;
  endDate?: string;
}
export const useSearchQuery = (searchParams: SearchParams) => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useSearchQuery.queryKey(searchParams),
    queryFn: () => useSearchQuery.queryFn(searchParams),
    enabled: Boolean(searchParams.searchValue || searchParams.startDate || searchParams.endDate),
  });

  return {
    searchResultsAreReady: isFetched,
    searchResults: data,
    refetchSearchResults: refetch,
  };
};
useSearchQuery.queryKey = ({ searchValue, startDate, endDate, cityId }: SearchParams) => [
  'search',
  'results',
  searchValue,
  startDate,
  endDate,
  cityId,
];
useSearchQuery.queryFn = ({ searchValue, startDate, endDate, cityId }: SearchParams) =>
  API.post<{ events: FeedEvent[]; venues: any[]; promoters: Promoter[] }, SearchParams>(
    `/v1/events/search`,
    {
      searchValue,
      startDate,
      endDate,
      cityId,
    },
  );

interface UserConversation {
  entertainerId?: string;
  userId?: string;
  promoterId?: string;
  isUnread: boolean;
  lastMessageAt: string;
  text?: string;
  title: string;
  _id: string;
}

export const useUserConversationsQuery = () => {
  const { userIsLoggedIn } = useAppContext();

  const { isFetched, data, refetch } = useQuery({
    queryKey: useUserConversationsQuery.queryKey(),
    queryFn: () => useUserConversationsQuery.queryFn(),
    enabled: userIsLoggedIn,
  });

  return {
    userConversationsAreReady: isFetched,
    userConversations: data?.conversations ?? { user: [], promoter: [], entertainer: [] },
    refetchUserConversations: refetch,
  };
};

useUserConversationsQuery.queryKey = (userId?: string) => ['conversations', userId];
useUserConversationsQuery.queryFn = () =>
  API.get<{
    conversations: {
      user: UserConversation[];
      promoter: UserConversation[];
      entertainer: UserConversation[];
    };
  }>('/v1/conversations/me');

export const useManageCompetitionsQuery = () => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useManageCompetitionsQuery.queryKey(),
    queryFn: useManageCompetitionsQuery.queryFn,
  });

  return {
    competitionsAreReady: isFetched,
    competitions: data?.campaigns,
    refetchCompetitions: refetch,
  };
};

useManageCompetitionsQuery.queryKey = () => ['competitions'];
useManageCompetitionsQuery.queryFn = () =>
  API.get<{ campaigns: Record<string, any[]> }>(`/v1/user/promoters/campaigns`);
export const fetchManageCompetitions = async (queryClient: QueryClient) => {
  await queryClient.invalidateQueries({ queryKey: useManageCompetitionsQuery.queryKey() });

  return queryClient.fetchQuery({
    queryKey: useManageCompetitionsQuery.queryKey(),
    queryFn: useManageCompetitionsQuery.queryFn,
  });
};

export const useUserChallengesQuery = ({ cityId }: { cityId?: string }) => {
  const { userIsLoggedIn } = useAppContext();

  const { isFetched, data, refetch } = useQuery({
    queryKey: useUserChallengesQuery.queryKey(),
    queryFn: () => useUserChallengesQuery.queryFn({ cityId }),
    enabled: userIsLoggedIn,
  });

  return {
    challengesAreReady: isFetched,
    challenges: data?.userChallenges,
    refetchChallenges: refetch,
  };
};

useUserChallengesQuery.queryKey = () => ['challenges'];
useUserChallengesQuery.queryFn = ({ cityId }: { cityId?: string }) => {
  const cityQuery = cityId ? `?cityId=${cityId}` : '';
  return API.get<{ userChallenges: any[] }>(`/v1/user/challenges${cityQuery}`);
};

export const useUserWalletQuery = () => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useUserWalletQuery.queryKey(),
    queryFn: () => useUserWalletQuery.queryFn(),
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
  });

  return {
    walletIsReady: isFetched,
    wallet: data,
    walletBalanceFormatted: data ? `$${(data.total / 100).toFixed(2)}` : '$0.00',
    refetchWallet: refetch,
  };
};
useUserWalletQuery.queryKey = () => ['wallet'];
useUserWalletQuery.queryFn = () => API.get<{ total: number }>(`/v1/user/wallet`);

export const useUserTalentQuery = () => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useUserTalentQuery.queryKey(),
    queryFn: () => useUserTalentQuery.queryFn(),
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
  });

  return {
    talentIsReady: isFetched,
    talent: data,
    refetchTalent: refetch,
  };
};
useUserTalentQuery.queryKey = () => ['talent'];
useUserTalentQuery.queryFn = () =>
  API.get<{ bookingOpportunities: any[]; bookingRequests: any[] }>(`/v1/user/talent`);

interface PublicDetailQuestion {
  _id: string;
  documentType: string;
  question: string;
  options: string[];
}

export const useDetailQuestionsQuery = () => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useDetailQuestionsQuery.queryKey(),
    queryFn: () => useDetailQuestionsQuery.queryFn(),
  });

  return {
    detailQuestionsAreReady: isFetched,
    detailQuestionsByDocumentType: data?.detailQuestionsByDocumentType,
    refetchDetailQuestions: refetch,
  };
};

useDetailQuestionsQuery.queryKey = () => ['detail-questions'];
useDetailQuestionsQuery.queryFn = () =>
  API.get<{
    detailQuestionsByDocumentType: {
      booking: PublicDetailQuestion[];
    };
  }>(`/v1/detail-questions`);

export const useUserDashboardNotificationsQuery = (
  { cityId = '' }: { cityId: string },
  options: {
    isEnabled?: boolean;
    refetchOnWindowFocus?: boolean;
    refetchOnReconnect?: boolean;
    refetchOnMount?: boolean;
  } = {},
) => {
  const {
    isEnabled = true,
    refetchOnWindowFocus = true,
    refetchOnReconnect = true,
    refetchOnMount = true,
  } = options ?? {};

  const { isFetched, data, refetch } = useQuery({
    queryKey: useUserDashboardNotificationsQuery.queryKey({ cityId }),
    queryFn: () => useUserDashboardNotificationsQuery.queryFn({ cityId }),
    enabled: !!cityId && isEnabled,
    refetchOnWindowFocus,
    refetchOnReconnect,
    refetchOnMount,
  });

  return {
    userDashboardNotificationsIsReady: Boolean(isFetched && cityId && data),
    userDashboardNotifications: data,
    refetchUserDashboardNotifications: refetch,
  };
};

useUserDashboardNotificationsQuery.queryKey = ({ cityId }: { cityId: string }) => [
  'entertainer-dashboard-notifications',
  cityId,
];
useUserDashboardNotificationsQuery.queryFn = ({ cityId }: { cityId: string }) =>
  cityId
    ? API.get<{ splashNotification: any }>(`/v1/user/dashboard-notifications?cityId=${cityId}`)
    : Promise.resolve<{}>({});

export const useEntertainerDashboardQuery = (
  { cityId = '' }: { cityId: string },
  options: {
    isEnabled?: boolean;
    refetchOnWindowFocus?: boolean;
    refetchOnReconnect?: boolean;
    refetchOnMount?: boolean;
  } = {},
) => {
  const {
    isEnabled = true,
    refetchOnWindowFocus = true,
    refetchOnReconnect = true,
    refetchOnMount = true,
  } = options ?? {};

  const { isFetched, data, refetch } = useQuery({
    queryKey: useEntertainerDashboardQuery.queryKey({ cityId }),
    queryFn: () => useEntertainerDashboardQuery.queryFn({ cityId }),
    enabled: !!cityId && isEnabled,
    refetchOnWindowFocus,
    refetchOnReconnect,
    refetchOnMount,
  });

  return {
    entertainerDashboardIsReady: Boolean(isFetched && cityId && data),
    entertainerDashboard: data,
    refetchEntertainerDashboard: refetch,
  };
};

useEntertainerDashboardQuery.queryKey = ({ cityId }: { cityId: string }) => [
  'entertainer-dashboard',
  cityId,
];
useEntertainerDashboardQuery.queryFn = ({ cityId }: { cityId: string }) =>
  cityId
    ? API.get<AppDashboard>(
        `/v1/user/entertainer/dashboard?cityId=${cityId}&disableSplashNotifications=true`,
      )
    : Promise.resolve<undefined>(undefined);

export const useNonEntertainerDashboardQuery = (
  { cityId }: { cityId: string },
  options: {
    isEnabled?: boolean;
    refetchOnWindowFocus?: boolean;
    refetchOnReconnect?: boolean;
    refetchOnMount?: boolean;
  } = {},
) => {
  const {
    isEnabled = true,
    refetchOnWindowFocus = true,
    refetchOnReconnect = true,
    refetchOnMount = true,
  } = options ?? {};

  const { isFetched, data, refetch } = useQuery({
    queryKey: useNonEntertainerDashboardQuery.queryKey({ cityId }),
    queryFn: () => useNonEntertainerDashboardQuery.queryFn({ cityId }),
    enabled: !!cityId && isEnabled,
    refetchOnWindowFocus,
    refetchOnReconnect,
    refetchOnMount,
  });

  return {
    nonEntertainerDashboardIsReady: Boolean(isFetched && cityId && data),
    nonEntertainerDashboard: data,
    refetchNonEntertainerDashboard: refetch,
  };
};

useNonEntertainerDashboardQuery.queryKey = ({ cityId }: { cityId: string }) => [
  'nonentertainer-dashboard',
  cityId,
];
useNonEntertainerDashboardQuery.queryFn = ({ cityId }: { cityId: string }) =>
  cityId
    ? API.get<AppDashboard>(`/v1/user/dashboard?cityId=${cityId}&disableSplashNotifications=true`)
    : Promise.resolve<undefined>(undefined);

export const useCurrentUserAppDashboard = (
  { cityId }: { cityId: string },
  options: {
    isEnabled?: boolean;
    refetchOnWindowFocus?: boolean;
    refetchOnReconnect?: boolean;
    refetchOnMount?: boolean;
  } = {},
) => {
  const { entertainer } = useCurrentEntertainerQuery();
  const { isEnabled = true } = options ?? {};
  const isEntertainer = Boolean(entertainer?._id);

  const { entertainerDashboardIsReady, entertainerDashboard, refetchEntertainerDashboard } =
    useEntertainerDashboardQuery(
      { cityId },
      {
        ...options,
        isEnabled: isEnabled && isEntertainer,
      },
    );
  const {
    nonEntertainerDashboardIsReady,
    nonEntertainerDashboard,
    refetchNonEntertainerDashboard,
  } = useNonEntertainerDashboardQuery(
    { cityId },
    {
      ...options,
      isEnabled: isEnabled && !isEntertainer,
    },
  );

  return {
    appDashboardIsReady: isEntertainer
      ? entertainerDashboardIsReady
      : nonEntertainerDashboardIsReady,
    appDashboard: isEntertainer ? entertainerDashboard : nonEntertainerDashboard,
    refetchAppDashboard: isEntertainer
      ? refetchEntertainerDashboard
      : refetchNonEntertainerDashboard,
  };
};
